import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearch } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useRegister } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { AuthShell, glassInput, glassInputGroup, glassInputBare, glassLabel, glassError, glassCta, glassLink } from "@/components/auth/AuthShell";

const signupSchema = z.object({
  name:     z.string().min(2, "Name is required"),
  username: z.string().min(3, "Username must be at least 3 characters").max(30, "Username must be 30 characters or less").regex(/^[a-z0-9_-]+$/, "Lowercase letters, numbers, _ and - only"),
  email:    z.string().email("Please enter a valid email"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type UsernameStatus = "idle" | "checking" | "available" | "unavailable";

export default function Signup() {
  const { toast } = useToast();
  const registerMutation = useRegister();
  const search = useSearch();
  const refCode = new URLSearchParams(search).get("ref") ?? undefined;

  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>("idle");
  const [usernameMessage, setUsernameMessage] = useState<string>("");
  const [usernameHint, setUsernameHint] = useState<string>("");
  const [usernameReason, setUsernameReason] = useState<string>("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const form = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    defaultValues: { name: "", username: "", email: "", password: "" },
  });

  const usernameValue = form.watch("username");

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = usernameValue?.trim().toLowerCase() ?? "";
    if (trimmed.length < 3) {
      setUsernameStatus("idle");
      setUsernameMessage("");
      setUsernameHint("");
      setUsernameReason("");
      return;
    }
    setUsernameStatus("checking");
    setUsernameMessage("");
    debounceRef.current = setTimeout(async () => {
      try {
        const base = import.meta.env.BASE_URL.replace(/\/$/, "");
        const res  = await fetch(`${base}/api/v1/check/username/${encodeURIComponent(trimmed)}`);
        const data = await res.json();
        if (data.available) {
          setUsernameStatus("available");
          setUsernameMessage("");
          setUsernameHint("");
          setUsernameReason("");
        } else {
          setUsernameStatus("unavailable");
          setUsernameMessage(data.message || "Username is not available.");
          setUsernameHint(data.hint || "");
          setUsernameReason(data.reason || "");
        }
      } catch {
        setUsernameStatus("idle");
        setUsernameMessage("");
        setUsernameHint("");
        setUsernameReason("");
      }
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [usernameValue]);

  const isSubmitDisabled = registerMutation.isPending || usernameStatus === "unavailable" || usernameStatus === "checking";
  const isReserved = usernameReason === "USERNAME_PLATFORM_RESERVED";
  const isTrademark = usernameReason === "USERNAME_TRADEMARK_PROTECTED";
  const isGeographic = usernameReason === "USERNAME_GEOGRAPHIC_RESERVED";
  const isWarning = isReserved || isTrademark || isGeographic;

  const onSubmit = (data: z.infer<typeof signupSchema>) => {
    if (usernameStatus !== "available" && usernameStatus !== "idle") return;
    registerMutation.mutate({ data: { ...data, ref: refCode } }, {
      onSuccess: (res) => {
        localStorage.setItem("commonqr_token", res.token);
        window.location.href = "/dashboard";
      },
      onError: (error: any) => {
        toast({ title: "Registration Failed", description: error?.message || "Username or email may already be taken.", variant: "destructive" });
      },
    });
  };

  const handleGoogleSignup = () => {
    window.location.href = "/api/v1/auth/google";
  };

  const UsernameIndicator = () => {
    if (usernameStatus === "checking")    return <Loader2 className="w-4 h-4 animate-spin cqr-faint absolute right-4 top-1/2 -translate-y-1/2" />;
    if (usernameStatus === "available")   return <CheckCircle2 className="w-4 h-4 cqr-ok absolute right-4 top-1/2 -translate-y-1/2" />;
    if (usernameStatus === "unavailable") return isWarning
      ? <AlertTriangle className="w-4 h-4 cqr-warn absolute right-4 top-1/2 -translate-y-1/2" />
      : <XCircle className="w-4 h-4 cqr-err absolute right-4 top-1/2 -translate-y-1/2" />;
    return null;
  };

  const usernameState =
    usernameStatus === "available" ? "ok"
    : usernameStatus === "unavailable" ? (isWarning ? "warn" : "err")
    : undefined;

  return (
    <AuthShell
      title="Create your Profile"
      subtitle="One link for everything you do."
      dividerText="or sign up with email"
      onGoogle={handleGoogleSignup}
      footer={
        <>
          Already have an account?{" "}
          <Link href="/login" className={`font-semibold ${glassLink}`}>Log in</Link>
        </>
      }
    >
      <Helmet>
        <title>Sign Up — CommonQR</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="signup-name" className={glassLabel}>Full Name</label>
          <input id="signup-name" placeholder="Jane Doe" autoComplete="name" {...form.register("name")} className={glassInput} />
          {form.formState.errors.name && <p className={glassError}>{form.formState.errors.name.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-username" className={glassLabel}>Username</label>
          <div className={glassInputGroup} data-state={usernameState}>
            <span className="cqr-faint select-none pointer-events-none">commonqr.com/</span>
            <input
              id="signup-username"
              placeholder="janedoe"
              autoComplete="username"
              {...form.register("username", { onChange: (e) => { e.target.value = e.target.value.toLowerCase(); } })}
              className={glassInputBare}
            />
            <UsernameIndicator />
          </div>

          {/* Status messages */}
          {usernameStatus === "available" && (
            <p className="cqr-ok text-sm mt-1.5 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Username is available!
            </p>
          )}
          {usernameStatus === "unavailable" && usernameMessage && (
            <div className="mt-1.5">
              <p className={`text-sm flex items-center gap-1 ${isWarning ? "cqr-warn" : "cqr-err"}`}>
                {isWarning
                  ? <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  : <XCircle className="w-3.5 h-3.5 shrink-0" />
                }
                {usernameMessage}
              </p>
              {usernameHint && (
                <p className="cqr-muted text-xs mt-1 ml-5">{usernameHint}</p>
              )}
              {(isTrademark || isGeographic) && (
                <a
                  href="/claim-username"
                  className="cqr-link text-xs font-medium mt-1.5 ml-5 inline-block"
                >
                  {isGeographic ? "Apply for free government verified ownership →" : "Claim this username as a verified brand (fee applies) →"}
                </a>
              )}
            </div>
          )}
          {form.formState.errors.username && usernameStatus === "idle" && (
            <p className={glassError}>{form.formState.errors.username.message}</p>
          )}
          <p className="cqr-faint text-xs mt-1.5">3–30 characters. Lowercase letters, numbers, _ and - only.</p>
        </div>

        <div>
          <label htmlFor="signup-email" className={glassLabel}>Email Address</label>
          <input id="signup-email" type="email" placeholder="you@example.com" autoComplete="email" {...form.register("email")} className={glassInput} />
          {form.formState.errors.email && <p className={glassError}>{form.formState.errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="signup-password" className={glassLabel}>Password</label>
          <input id="signup-password" type="password" placeholder="••••••••" autoComplete="new-password" {...form.register("password")} className={glassInput} />
          {form.formState.errors.password && <p className={glassError}>{form.formState.errors.password.message}</p>}
        </div>

        <button type="submit" disabled={isSubmitDisabled} className={`${glassCta} !mt-6`}>
          {registerMutation.isPending
            ? <Loader2 className="w-5 h-5 animate-spin" />
            : usernameStatus === "checking"
            ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking username…</>
            : "Sign Up"
          }
        </button>
      </form>
    </AuthShell>
  );
}
